// src/components/NavigationItem.tsx

import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
    alpha,
    Box,
    Collapse,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    useTheme,
} from '@mui/material';
import { GoChevronRight } from 'react-icons/go';

import Icon from './Icon'; // Assuming Icon.tsx is in the same directory
import type { Navigation } from '@/types/navigation';

interface NavigationItemProps {
    item: Navigation;
    open: boolean; // Is the main drawer open?
}

export const NavigationItem: React.FC<NavigationItemProps> = ({ item, open }) => {
    const theme = useTheme();
    const location = useLocation();

    // Determine if the current item or one of its children is active
    const hasActiveChild = item.subNavigation?.some(
        (child) => location.pathname.startsWith(child.link)
    ) ?? false;

    const isActive = location.pathname === item.link || hasActiveChild;

    const [isCollapsed, setIsCollapsed] = useState(isActive); // Expand if active on initial render

    const hasSubNavigation = item.subNavigation && item.subNavigation.length > 0;

    // If the active path changes, check if we need to expand this item
    useEffect(() => {
        if (hasActiveChild) {
            setIsCollapsed(true);
        }
    }, [location.pathname, hasActiveChild]);

    const handleToggleCollapse = () => {
        if (hasSubNavigation) {
            setIsCollapsed((prev) => !prev);
        }
    };

    return (
        <>
            <ListItemButton
                component={hasSubNavigation ? 'div' : RouterLink}
                to={!hasSubNavigation ? item.link : undefined}
                onClick={handleToggleCollapse}
                sx={{
                    mx: 1,
                    borderRadius: 1,
                    mb: 0.5,
                    // --- STYLE CORRECTIONS START HERE ---
                    paddingBottom: 0.5,
                    paddingTop: 0.5,
                    // REPLICATE: Set background color for active items (that are not parents)
                    backgroundColor: isActive && !hasSubNavigation
                        ? (theme.palette.mode === 'light' ? theme.palette.primary.main : 'rgba(255, 255, 255, 0.05)')
                        : 'transparent',

                    // REPLICATE: Set the text and icon color based on active state and mode
                    color: isActive && !hasSubNavigation
                        ? 'white' // Active items get white text on the primary background
                        : (theme.palette.mode === 'light'
                            ? alpha(theme.palette.text.primary, 0.7)
                            : 'rgba(255, 255, 255, 0.4)'),

                    // REPLICATE: Set the hover state styles
                    '&:hover': {
                        backgroundColor: theme.palette.mode === 'light'
                            ? alpha(theme.palette.primary.main, 0.4)
                            : 'rgba(255, 255, 255, 0.05)',
                        // Also adjust text color on hover to match original intent
                        color: theme.palette.mode === 'dark' ? 'white' : theme.palette.primary.main,
                    },
                    // --- STYLE CORRECTIONS END HERE ---
                }}
            >
                <ListItemIcon sx={{ minWidth: 'auto', mr: open ? 1.5 : 'auto', color: 'inherit' }}>
                    <Icon icon={item.image} />
                </ListItemIcon>

                {open && (
                    <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                            fontSize: '12px',
                            fontWeight: 500,
                            textTransform: 'capitalize',
                        }}
                    />
                )}

                {open && hasSubNavigation && (
                    <Box
                        sx={{
                            transition: 'transform 0.2s',
                            transform: isCollapsed ? 'rotate(90deg)' : 'rotate(0deg)',
                        }}
                    >
                        <GoChevronRight />
                    </Box>
                )}
            </ListItemButton>

            {open && hasSubNavigation && (
                <Collapse in={isCollapsed} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ pl: 2.5 }}>
                        {item?.subNavigation?.map((child) => (
                            <NavigationItem key={child.id} item={child} open={open} />
                        ))}
                    </List>
                </Collapse>
            )}
        </>
    );
};